import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  CircularProgress,
  Alert,
  FormControl,
  Autocomplete,
  TextField,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  Warning,
  CheckCircle,
  ErrorOutline,
  Visibility,
  TrendingUp,
  Save,
  AccessTime,
} from '@mui/icons-material';
import { getAccessToken, getDepartment, getUsername } from '../../utils/storage';
import JornadaExportButtons from '../../components/JornadaExportButtons';

// TypeScript DTOs from Backend
interface JourneySummary {
  monitorados: number;
  conformes: number;
  parcialmenteConformes: number;
  críticos: number;
  complianceGeral: number;

  totalClients: number;
  clientsHealthy: number;
  clientsAttention: number;
  clientsCritical: number;
  clientsAbandoned: number;
  averageJourneyScore: number;
  averagePendingPerClient: number;
  averageDaysWithoutMeeting: number;
  medianDaysWithoutMeeting: number;

  daysWithoutMeetingDistribution: {
    range_0_30: number;
    range_31_60: number;
    range_61_90: number;
    range_above_90: number;
  };

  totalMetasPrevistas: number;
  totalMetasCumpridas: number;
  meetingComplianceRate: number;

  clientsWithoutRD: number;
  clientsWithoutRELast45Days: number;
  clientsWithoutRCInPeriod: number;
  clientsWithoutRAEInPeriod: number;
}

interface ClientListItem {
  id_loja: number;
  razao_social: string;
  consultor: string;
  complianceRate: number;
  totalPending: number;
  priorityScore: number;
  priorityReasons: string[];
  daysSinceLastMeeting: number | null;
  riskLevel: string;
  expectedMeetings: number;
  completedMeetings: number;
  isMissingRD: boolean;
  isMissingRE45: boolean;
  isMissingRC: boolean;
  isMissingRAE: boolean;
}

interface CompanyJourneyData {
  summary: JourneySummary;
  priority: {
    critical: ClientListItem[];
    warning: ClientListItem[];
    healthy: ClientListItem[];
    unmonitored: ClientListItem[];
  };
  priorityRanking: ClientListItem[];
}

interface ConsultantMeetingSummaryDto {
  meta: number;
  completed: number;
  pending: number;
  compliance: number;
  status: string;
}

interface ConsultantJourneyResponseDto {
  consultor: string;
  summary: Record<string, ConsultantMeetingSummaryDto>;
  totalMeetingsExpected: number;
  totalMeetingsCompleted: number;
  totalMeetingsPending: number;
  averageJourneyScore: number;
  clientsWithoutMeeting30Days: number;
  clientsWithoutMeeting60Days: number;
  clientsWithoutMeeting90Days: number;
}

interface ClientMeetingJourney {
  type: string;
  meetingType: string;
  meta: number;
  expected: number;
  completed: number;
  pending: number;
  compliance: number;
  status: string;
  riskLevel: string;
  lastCompleted: string | null;
  lastDue: string | null;
  nextDue: string | null;
  daysSinceLastMeeting: number | null;
  daysOverdue: number;
}

interface ClientJourneyDetail {
  id_loja: number;
  cliente: string;
  cnpj: string;
  consultor: string;
  journeyScore: number;
  data_inicio_monitoramento: string | null;
  riskLevel: string;
  lastMeetingType: string | null;
  lastCompleted: string | null;
  daysSinceLastMeeting: number | null;
  meetings: ClientMeetingJourney[];
}

interface ConsultantOption {
  value: string;
  label: string;
}

const glassCardStyle = {
  background: 'rgba(25, 30, 45, 0.65)',
  backdropFilter: 'blur(16px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '16px',
  color: 'white',
  boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.25)',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
};

const hoverCardStyle = {
  ...glassCardStyle,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px 0 rgba(0, 0, 0, 0.35)',
  },
};

// Safe Display Format Helpers
const formatDisplayDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '—';
    const cleanStr = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const parts = cleanStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  } catch {
    return '—';
  }
};

const formatDisplayValue = (val: any): string | number => {
  if (val === null || val === undefined || (typeof val === 'number' && isNaN(val))) {
    return '—';
  }
  return val;
};

// Risk levels unified color schema
const getRiskLevelStyles = (risk: string | null | undefined) => {
  switch (risk) {
    case 'HEALTHY':
      return { label: 'Saudável', color: '#06d6a0', bg: 'rgba(6, 214, 160, 0.15)' };
    case 'ATTENTION':
      return { label: 'Atenção', color: '#ffd166', bg: 'rgba(253, 209, 102, 0.15)' };
    case 'CRITICAL':
      return { label: 'Crítico', color: '#ff9f43', bg: 'rgba(255, 159, 67, 0.15)' };
    case 'ABANDONED':
      return { label: 'Abandonado', color: '#ff6b6b', bg: 'rgba(255, 107, 107, 0.15)' };
    case 'NOT_STARTED':
    default:
      return { label: 'Monitoramento ainda não iniciado', color: '#8b949e', bg: 'rgba(139, 148, 158, 0.15)' };
  }
};

// Meeting Status label mapper - Section 2 & 4
const getMeetingStatusLabel = (status: string | null | undefined, expected: number, nextDue: string | null) => {
  if (expected === 0 && nextDue) {
    return 'Ainda não exigida';
  }
  switch (status) {
    case 'OK':
      return 'Em dia';
    case 'WARNING':
      return 'Parcialmente cumprida';
    case 'OVERDUE':
      return 'Atrasada';
    case 'NOT_APPLICABLE':
    default:
      return 'Sem obrigação no período';
  }
};

// Meeting Status unified color schema
const getMeetingStatusColorStyles = (status: string | null | undefined, expected: number, nextDue: string | null) => {
  if (expected === 0 && nextDue) {
    return { color: '#64b5f6', bg: 'rgba(100, 181, 246, 0.1)' }; // Clean blue/info for scheduled future
  }
  switch (status) {
    case 'OK':
      return { color: '#06d6a0', bg: 'rgba(6, 214, 160, 0.1)' };
    case 'WARNING':
      return { color: '#ffd166', bg: 'rgba(253, 209, 102, 0.1)' };
    case 'OVERDUE':
      return { color: '#ff6b6b', bg: 'rgba(255, 107, 107, 0.1)' };
    case 'NOT_APPLICABLE':
    default:
      return { color: '#8b949e', bg: 'rgba(139, 148, 158, 0.1)' };
  }
};

// Helper to extract logged-in user from JWT token
const getUserFromToken = (): string | null => {
  try {
    const token = getAccessToken();
    if (!token) return null;
    const base64Payload = token.split('.')[1];
    const payload = JSON.parse(atob(base64Payload));
    return payload.sub || payload.user || payload.username || payload.name || null;
  } catch {
    return null;
  }
};

const JornadaDoCliente = () => {
  const [data, setData] = useState<CompanyJourneyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dashboardRef = React.useRef<HTMLDivElement>(null);

  // User role restrictions
  const [resolvedDepartment, setResolvedDepartment] = useState<string | null>(null);
  const loggedInConsultant = getUserFromToken();

  // Computed department: localStorage first, fallback to API-resolved
  const userDepartment = getDepartment() || resolvedDepartment;
  const isConsultorUser = userDepartment === 'Consultor';
  const currentLoggedUser = getUsername() || loggedInConsultant;

  // Fallback: if department is empty, try to fetch from API
  useEffect(() => {
    const storedDept = getDepartment();
    if (!storedDept && loggedInConsultant) {
      const fetchDepartment = async () => {
        try {
          const token = getAccessToken();
          const resp = await axios.get(
            `${process.env.REACT_APP_API_URL}/login/get/user/${loggedInConsultant}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const dept = resp.data?.department || '';
          if (dept) {
            setResolvedDepartment(dept);
          }
        } catch (e) {
          console.error('Failed to resolve department:', e);
        }
      };
      fetchDepartment();
    }
  }, [loggedInConsultant]);

  useEffect(() => {
    const loadConsultantDirectory = async () => {
      if (!currentLoggedUser) {
        setConsultantDirectory([]);
        return;
      }

      if (isConsultorUser) {
        setConsultantDirectory([{ value: currentLoggedUser, label: 'Minha Carteira' }]);
        return;
      }

      try {
        const token = getAccessToken();
        if (!token) {
          throw new Error('Token de acesso não encontrado');
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/login/department/username/Consultor`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const consultors = Array.isArray(response.data) ? response.data : [];
        const directory = consultors
          .map((item: { user?: string; nome?: string } | string) => {
            if (typeof item === 'string') {
              return { value: item, label: item };
            }

            const value = item.user || item.nome || '';
            return value ? { value, label: item.nome || item.user || value } : null;
          })
          .filter((item): item is ConsultantOption => item !== null);

        setConsultantDirectory(directory);
      } catch (error) {
        console.error('Erro ao carregar consultores para o filtro:', error);
        setConsultantDirectory([]);
      }
    };

    loadConsultantDirectory();
  }, [currentLoggedUser, isConsultorUser]);

  // Consultant-specific Dashboard Data
  const [consultantData, setConsultantData] = useState<ConsultantJourneyResponseDto | null>(null);
  const [consultantLoading, setConsultantLoading] = useState(false);
  const [consultantDirectory, setConsultantDirectory] = useState<ConsultantOption[]>([]);

  // Filters state
  const [filterMode, setFilterMode] = useState<'reference' | 'period'>('reference');

  // Reference date (single cut-off)
  const [referenceDate, setReferenceDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  // Period range (two dates)
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [toDate, setToDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  const [selectedConsultant, setSelectedConsultant] = useState<string>('');

  // Tabs control
  const [tabValue, setTabValue] = useState(0);

  // Detail Modal control
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<ClientJourneyDetail | null>(null);
  const [selectedClient, setSelectedClient] = useState<ClientListItem | null>(null);

  // Edit Monitoring Date state
  const [monitoringDate, setMonitoringDate] = useState('');
  const [savingDate, setSavingDate] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastOpen, setToastOpen] = useState(false);

  const fetchDashboardData = useCallback(async (
    mode: 'reference' | 'period',
    refDate: string,
    periodFrom?: string,
    periodTo?: string,
  ) => {
    setLoading(true);
    setError(null);
    try {
      const token = getAccessToken();
      let url: string;
      if (mode === 'period' && periodFrom && periodTo) {
        url = `${process.env.REACT_APP_API_URL}/dashboard/journey/period?from=${periodFrom}&to=${periodTo}`;
      } else {
        url = `${process.env.REACT_APP_API_URL}/dashboard/journey?date=${refDate}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
    } catch (err: any) {
      console.error(err);
      setError('Erro ao carregar dados do painel de jornada. Verifique a conexão com o servidor.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch company dashboard data
  useEffect(() => {
    fetchDashboardData(filterMode, referenceDate, fromDate, toDate);
  }, [filterMode, referenceDate, fromDate, toDate, fetchDashboardData]);

  // Auto-select the current user for Consultor profiles only
  useEffect(() => {
    if (isConsultorUser && currentLoggedUser) {
      setSelectedConsultant(currentLoggedUser);
    }
  }, [isConsultorUser, currentLoggedUser]);

  // Fetch consultant dashboard data when a consultant is selected
  useEffect(() => {
    if (selectedConsultant !== '__ALL__') {
      setConsultantLoading(true);
      const fetchConsultantData = async () => {
        try {
          const token = getAccessToken();
          let url: string;
          if (filterMode === 'period' && fromDate && toDate) {
            url = `${process.env.REACT_APP_API_URL}/dashboard/journey/consultor/${selectedConsultant}/period?from=${fromDate}&to=${toDate}`;
          } else {
            url = `${process.env.REACT_APP_API_URL}/dashboard/journey/consultor/${selectedConsultant}?date=${referenceDate}`;
          }
          const response = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setConsultantData(response.data);
        } catch (err: any) {
          console.error(err);
        } finally {
          setConsultantLoading(false);
        }
      };
      fetchConsultantData();
    } else {
      setConsultantData(null);
    }
  }, [selectedConsultant, referenceDate, filterMode, fromDate, toDate]);

  const handleInspectClient = async (client: ClientListItem) => {
    setSelectedClient(client);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const token = getAccessToken();
      let url: string;
      if (filterMode === 'period' && fromDate && toDate) {
        url = `${process.env.REACT_APP_API_URL}/dashboard/journey/client/${client.id_loja}/period?from=${fromDate}&to=${toDate}`;
      } else {
        url = `${process.env.REACT_APP_API_URL}/dashboard/journey/client/${client.id_loja}?date=${referenceDate}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const detail: ClientJourneyDetail = response.data;
      setDetailData(detail);
      setMonitoringDate(detail.data_inicio_monitoramento ? detail.data_inicio_monitoramento.split('T')[0] : '');
    } catch (err: any) {
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchClientDetail = useCallback(async (id_loja: number) => {
    try {
      const token = getAccessToken();
      let url: string;
      if (filterMode === 'period' && fromDate && toDate) {
        url = `${process.env.REACT_APP_API_URL}/dashboard/journey/client/${id_loja}/period?from=${fromDate}&to=${toDate}`;
      } else {
        url = `${process.env.REACT_APP_API_URL}/dashboard/journey/client/${id_loja}?date=${referenceDate}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (err: any) {
      console.error(`Erro ao buscar detalhamento do cliente ${id_loja}:`, err);
      return null;
    }
  }, [referenceDate, filterMode, fromDate, toDate]);

  const handleSaveMonitoringDate = async () => {
    if (!detailData || !selectedClient) return;
    setSavingDate(true);
    try {
      const token = getAccessToken();
      const payload = {
        data_inicio_monitoramento: monitoringDate ? `${monitoringDate}T00:00:00.000Z` : null,
      };
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/loja/update/${detailData.cnpj}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setToastMessage('Configuração de monitoramento salva com sucesso!');
      setToastOpen(true);
      setDetailOpen(false);
      setDetailData(null);
      setSelectedClient(null);
      fetchDashboardData(filterMode, referenceDate, fromDate, toDate);
    } catch (err: any) {
      console.error(err);
      alert('Erro ao salvar a data de monitoramento.');
    } finally {
      setSavingDate(false);
    }
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setDetailData(null);
    setSelectedClient(null);
    setMonitoringDate('');
  };

  // Extract unique list of consultants for dropdown
  const consultantsList = React.useMemo<string[]>(() => {
    if (!data) return [];
    const list = [
      ...data.priority.critical.map((c: ClientListItem) => c.consultor),
      ...data.priority.warning.map((c: ClientListItem) => c.consultor),
      ...data.priority.healthy.map((c: ClientListItem) => c.consultor),
      ...data.priority.unmonitored.map((c: ClientListItem) => c.consultor),
    ];
    return Array.from(new Set(list.filter(Boolean)));
  }, [data]);

  const consultantOptions = React.useMemo<ConsultantOption[]>(() => {
    if (isConsultorUser) {
      return consultantDirectory;
    }

    const fallbackDirectory = consultantDirectory.length > 0
      ? consultantDirectory
      : consultantsList.map((consultor) => ({ value: consultor, label: consultor }));

    return [
      { value: '__ALL__', label: 'Todos os Consultores (Empresa)' },
      ...fallbackDirectory,
    ];
  }, [consultantsList, consultantDirectory, isConsultorUser]);

  const selectedConsultantOption = React.useMemo(() => {
    return consultantOptions.find((option) => option.value === selectedConsultant) || null;
  }, [consultantOptions, selectedConsultant]);

  // Client-side filtering by Consultant for table lists
  const filteredCritical = React.useMemo(() => {
    const list = data?.priority.critical || [];
    return list.filter((c) => selectedConsultant === '__ALL__' || c.consultor === selectedConsultant);
  }, [data, selectedConsultant]);

  const filteredWarning = React.useMemo(() => {
    const list = data?.priority.warning || [];
    return list.filter((c) => selectedConsultant === '__ALL__' || c.consultor === selectedConsultant);
  }, [data, selectedConsultant]);

  const filteredHealthy = React.useMemo(() => {
    const list = data?.priority.healthy || [];
    return list.filter((c) => selectedConsultant === '__ALL__' || c.consultor === selectedConsultant);
  }, [data, selectedConsultant]);

  const filteredUnmonitored = React.useMemo(() => {
    const list = data?.priority.unmonitored || [];
    return list.filter((c) => selectedConsultant === '__ALL__' || c.consultor === selectedConsultant);
  }, [data, selectedConsultant]);

  const filteredRanking = React.useMemo(() => {
    const list = data?.priorityRanking || [];
    return list.filter((c) => selectedConsultant === '__ALL__' || c.consultor === selectedConsultant);
  }, [data, selectedConsultant]);

  // Dados filtrados para exportação respeitando o consultor selecionado
  const exportData = React.useMemo(() => {
    if (!data) return null;
    if (selectedConsultant === '__ALL__') return data;

    const allFiltered = [
      ...filteredCritical,
      ...filteredWarning,
      ...filteredHealthy,
      ...filteredUnmonitored,
    ];
    const monitored = [
      ...filteredCritical,
      ...filteredWarning,
      ...filteredHealthy,
    ];

    const daysList = monitored
      .map((c) => c.daysSinceLastMeeting)
      .filter((d): d is number => d !== null);
    const sortedDays = [...daysList].sort((a, b) => a - b);
    const medianDays = sortedDays.length > 0
      ? sortedDays.length % 2 === 0
        ? (sortedDays[sortedDays.length / 2 - 1] + sortedDays[sortedDays.length / 2]) / 2
        : sortedDays[Math.floor(sortedDays.length / 2)]
      : 0;

    const totalMetasPrevistas = monitored.reduce((s, c) => s + (c.expectedMeetings || 0), 0);
    const totalMetasCumpridas = monitored.reduce((s, c) => s + (c.completedMeetings || 0), 0);

    const filteredSummary: JourneySummary = {
      ...data.summary,
      totalClients: allFiltered.length,
      monitorados: monitored.length,
      clientsHealthy: filteredHealthy.length,
      clientsAttention: filteredWarning.length,
      clientsCritical: filteredCritical.length,
      clientsAbandoned: filteredUnmonitored.filter((c) => c.riskLevel === 'ABANDONED').length,
      averageJourneyScore: monitored.length > 0
        ? monitored.reduce((s, c) => s + (c.complianceRate || 0), 0) / monitored.length
        : 0,
      averagePendingPerClient: monitored.length > 0
        ? monitored.reduce((s, c) => s + (c.totalPending || 0), 0) / monitored.length
        : 0,
      averageDaysWithoutMeeting: daysList.length > 0
        ? daysList.reduce((a, b) => a + b, 0) / daysList.length
        : 0,
      medianDaysWithoutMeeting: medianDays,
      daysWithoutMeetingDistribution: {
        range_0_30: daysList.filter((d) => d >= 0 && d <= 30).length,
        range_31_60: daysList.filter((d) => d >= 31 && d <= 60).length,
        range_61_90: daysList.filter((d) => d >= 61 && d <= 90).length,
        range_above_90: daysList.filter((d) => d > 90).length,
      },
      totalMetasPrevistas,
      totalMetasCumpridas,
      meetingComplianceRate: totalMetasPrevistas > 0 ? Number(((totalMetasCumpridas / totalMetasPrevistas) * 100).toFixed(2)) : 0,
      clientsWithoutRD: monitored.filter((c) => c.isMissingRD).length,
      clientsWithoutRELast45Days: monitored.filter((c) => c.isMissingRE45).length,
      clientsWithoutRCInPeriod: monitored.filter((c) => c.isMissingRC).length,
      clientsWithoutRAEInPeriod: monitored.filter((c) => c.isMissingRAE).length,
    };

    return {
      ...data,
      summary: filteredSummary,
      priority: {
        critical: filteredCritical,
        warning: filteredWarning,
        healthy: filteredHealthy,
        unmonitored: filteredUnmonitored,
      },
      priorityRanking: filteredRanking,
    };
  }, [data, selectedConsultant, filteredCritical, filteredWarning, filteredHealthy, filteredUnmonitored, filteredRanking]);

  const activeList =
    tabValue === 0
      ? filteredCritical
      : tabValue === 1
      ? filteredWarning
      : tabValue === 2
      ? filteredHealthy
      : tabValue === 3
      ? filteredUnmonitored
      : filteredRanking;

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 64px)',
          backgroundImage: `url('/Background.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <CircularProgress color="primary" size={60} />
        <Typography variant="h6" sx={{ mt: 2, color: 'white', fontWeight: 600 }}>
          Carregando a Jornada do Cliente...
        </Typography>
      </Box>
    );
  }

  const summaryMetrics = data?.summary;

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)',
        width: '100%',
        backgroundImage: `url('/Background.jpeg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        {/* Export Buttons Bar */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <JornadaExportButtons
            data={exportData}
            fetchClientDetail={fetchClientDetail}
            fileNamePrefix={selectedConsultant !== '__ALL__' ? `jornada_cliente_${selectedConsultant.replace(/\s+/g, '_')}` : 'jornada_cliente_geral'}
          />
        </Box>

        {/* Exportable Report Content Container - captures page content only, ignoring sidebar and topbar */}
        <Box ref={dashboardRef} sx={{ p: 1, borderRadius: '16px' }}>
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: 'center', color: 'white' }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 800, letterSpacing: '-0.5px', textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}
            >
              Acompanhamento da Jornada do Cliente
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ mt: 1, opacity: 0.85, fontWeight: 500, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
            >
              Gestão inteligente de entregas e metodologia FAST
            </Typography>
          </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 4, borderRadius: '12px' }}>
            {error}
          </Alert>
        )}

        {/* Filters Panel */}
        <Paper
          sx={{
            p: 3,
            mb: 4,
            background: 'rgba(25, 30, 45, 0.65)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            color: 'white',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Filtros do Painel
          </Typography>

          {/* Mode Toggle */}
          <Box sx={{ mb: 2 }}>
            <Button
              variant={filterMode === 'reference' ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => setFilterMode('reference')}
              sx={{ mr: 1, borderRadius: '8px', fontWeight: 700, textTransform: 'none' }}
            >
              📅 Data de Referência
            </Button>
            <Button
              variant={filterMode === 'period' ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => setFilterMode('period')}
              sx={{ borderRadius: '8px', fontWeight: 700, textTransform: 'none' }}
            >
              📆 Período
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-end' }}>
            <FormControl sx={{ minWidth: 220 }}>
              <Autocomplete
                options={consultantOptions}
                value={selectedConsultantOption}
                onChange={(_: React.SyntheticEvent, option: ConsultantOption | null) => {
                  setSelectedConsultant(option?.value || '__ALL__');
                  setTabValue(0);
                }}
                getOptionLabel={(option: ConsultantOption) => option.label}
                isOptionEqualToValue={(option: ConsultantOption, value: ConsultantOption | null) => option.value === value?.value}
                disableClearable={isConsultorUser}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={isConsultorUser ? 'Minha Carteira' : 'Análise por Consultor'}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.08)',
                      borderRadius: '8px',
                      minWidth: 220,
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                        '&.Mui-focused fieldset': { borderColor: 'white' },
                      },
                      '& .MuiInputLabel-root': { color: 'white' },
                    }}
                  />
                )}
                sx={{
                  minWidth: 320,
                  '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
                    color: 'white',
                  },
                  '& .MuiInputBase-input': {
                    color: 'white',
                  },
                }}
                disabled={isConsultorUser && consultantOptions.length === 0}
                noOptionsText="Nenhum consultor disponível"
              />
            </FormControl>

            {filterMode === 'reference' ? (
              <TextField
                label="Data de Referência"
                type="date"
                value={referenceDate}
                onChange={(e) => setReferenceDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  minWidth: 220,
                  background: 'rgba(255, 255, 255, 0.08)',
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: 'white' },
                  },
                  '& .MuiInputLabel-root': { color: 'white' },
                }}
              />
            ) : (
              <>
                <TextField
                  label="Data Inicial"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    minWidth: 160,
                    background: 'rgba(255, 255, 255, 0.08)',
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                      '&.Mui-focused fieldset': { borderColor: 'white' },
                    },
                    '& .MuiInputLabel-root': { color: 'white' },
                  }}
                />
                <TextField
                  label="Data Final"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    minWidth: 160,
                    background: 'rgba(255, 255, 255, 0.08)',
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                      '&.Mui-focused fieldset': { borderColor: 'white' },
                    },
                    '& .MuiInputLabel-root': { color: 'white' },
                  }}
                />
              </>
            )}
          </Box>
        </Paper>

        {/* Dashboard Content Switch: Consultant View vs Company View */}
        {selectedConsultant !== '__ALL__' ? (
          /* ──────────────── CONSULTANT DASHBOARD VIEW ──────────────── */
          consultantLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : consultantData ? (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 2 }}>
                👤 Indicadores da Carteira do Consultor: <span style={{ color: '#ffd166' }}>{selectedConsultant}</span>
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                  gap: 3,
                  mb: 3,
                }}
              >
                <Card sx={hoverCardStyle}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <TrendingUp color="primary" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 600 }}>
                      Média Cumprimento da Meta
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5 }}>
                      {formatDisplayValue(consultantData.averageJourneyScore)}%
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={hoverCardStyle}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <CheckCircle sx={{ fontSize: 32, color: '#06d6a0', mb: 1 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 600 }}>
                      Total Esperadas
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5 }}>
                      {formatDisplayValue(consultantData.totalMeetingsExpected)}
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={hoverCardStyle}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <CheckCircle sx={{ fontSize: 32, color: '#06d6a0', mb: 1 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 600 }}>
                      Total Realizadas
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: '#06d6a0' }}>
                      {formatDisplayValue(consultantData.totalMeetingsCompleted)}
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={hoverCardStyle}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Warning sx={{ fontSize: 32, color: '#ff6b6b', mb: 1 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 600 }}>
                      Total Pendentes
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: '#ff6b6b' }}>
                      {formatDisplayValue(consultantData.totalMeetingsPending)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Inactivity Threshold cards for Consultant */}
              <Paper sx={{ p: 3, mb: 4, ...glassCardStyle }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  ⏳ Inatividade de Clientes (Sem atendimento operacional)
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                  <Paper sx={{ p: 2, background: 'rgba(255,255,255,0.05)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.07)', textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>Clientes sem atendimento &gt; 30 dias</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: consultantData.clientsWithoutMeeting30Days > 0 ? '#ffd166' : 'inherit' }}>
                      {formatDisplayValue(consultantData.clientsWithoutMeeting30Days)}
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, background: 'rgba(255,255,255,0.05)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.07)', textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>Clientes sem atendimento &gt; 60 dias</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: consultantData.clientsWithoutMeeting60Days > 0 ? '#ff9f43' : 'inherit' }}>
                      {formatDisplayValue(consultantData.clientsWithoutMeeting60Days)}
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, background: 'rgba(255,255,255,0.05)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.07)', textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>Clientes sem atendimento &gt; 90 dias</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: consultantData.clientsWithoutMeeting90Days > 0 ? '#ff6b6b' : 'inherit' }}>
                      {formatDisplayValue(consultantData.clientsWithoutMeeting90Days)}
                    </Typography>
                  </Paper>
                </Box>
              </Paper>
            </Box>
          ) : (
            <Alert severity="info" sx={{ mb: 4 }}>Erro ao recuperar indicadores do consultor.</Alert>
          )
        ) : (
          /* ──────────────── COMPANY DASHBOARD VIEW ──────────────── */
          summaryMetrics ? (
            <Box>
              {/* Portfolio Risk Distribution cards */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
                  gap: 3,
                  mb: 4,
                }}
              >
                <Card sx={hoverCardStyle}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <CheckCircle sx={{ fontSize: 32, color: '#06d6a0', mb: 1 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 600 }}>
                      Saudáveis
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: '#06d6a0' }}>
                      {formatDisplayValue(summaryMetrics.clientsHealthy)}
                    </Typography>
                  </CardContent>
                </Card>

                <Card sx={hoverCardStyle}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Warning sx={{ fontSize: 32, color: '#ffd166', mb: 1 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 600 }}>
                      Atenção
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: '#ffd166' }}>
                      {formatDisplayValue(summaryMetrics.clientsAttention)}
                    </Typography>
                  </CardContent>
                </Card>

                <Card sx={hoverCardStyle}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <ErrorOutline sx={{ fontSize: 32, color: '#ff9f43', mb: 1 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 600 }}>
                      Críticos
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: '#ff9f43' }}>
                      {formatDisplayValue(summaryMetrics.clientsCritical)}
                    </Typography>
                  </CardContent>
                </Card>

                <Card sx={hoverCardStyle}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <ErrorOutline sx={{ fontSize: 32, color: '#ff6b6b', mb: 1 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 600 }}>
                      Abandonados
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: '#ff6b6b' }}>
                      {formatDisplayValue(summaryMetrics.clientsAbandoned)}
                    </Typography>
                  </CardContent>
                </Card>

                <Card sx={hoverCardStyle}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <AccessTime sx={{ fontSize: 32, color: '#8b949e', mb: 1 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 600 }}>
                      Não Iniciados
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: '#8b949e' }}>
                      {formatDisplayValue(summaryMetrics.totalClients - summaryMetrics.monitorados)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Performance indicators */}
              <Box sx={{ mb: 4, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Paper sx={{ p: 3, ...glassCardStyle }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                    📊 Performance & Tempo de Atendimento
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>Média Dias Sem Reunião</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>{formatDisplayValue(summaryMetrics.averageDaysWithoutMeeting)} dias</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>Mediana Dias Sem Reunião</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>{formatDisplayValue(summaryMetrics.medianDaysWithoutMeeting)} dias</Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Distribuição de Atendimento (Inatividade):</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption">0–30 dias</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>{formatDisplayValue(summaryMetrics.daysWithoutMeetingDistribution?.range_0_30)} clientes</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption">31–60 dias</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>{formatDisplayValue(summaryMetrics.daysWithoutMeetingDistribution?.range_31_60)} clientes</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption">61–90 dias</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>{formatDisplayValue(summaryMetrics.daysWithoutMeetingDistribution?.range_61_90)} clientes</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption">acima de 90 dias</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>{formatDisplayValue(summaryMetrics.daysWithoutMeetingDistribution?.range_above_90)} clientes</Typography>
                    </Box>
                  </Box>
                </Paper>

                <Paper sx={{ p: 3, ...glassCardStyle, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                      🎯 Cumprimento Geral de Metas
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>Média Cumprimento de Metas: <strong>{formatDisplayValue(summaryMetrics.averageJourneyScore)}%</strong></Typography>
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>Média Pendências/Cliente: <strong>{formatDisplayValue(summaryMetrics.averagePendingPerClient)}</strong></Typography>
                        <Typography variant="body2" sx={{ opacity: 0.7, mt: 1 }}>Metas Previstas: <strong>{formatDisplayValue(summaryMetrics.totalMetasPrevistas)}</strong></Typography>
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>Metas Cumpridas: <strong>{formatDisplayValue(summaryMetrics.totalMetasCumpridas)}</strong></Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>Cumprimento de Meta Geral</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 850, color: '#06d6a0' }}>{formatDisplayValue(summaryMetrics.meetingComplianceRate)}%</Typography>
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={typeof summaryMetrics.meetingComplianceRate === 'number' ? summaryMetrics.meetingComplianceRate : 0}
                      sx={{
                        height: 12,
                        borderRadius: 6,
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        '& .MuiLinearProgress-bar': { backgroundColor: '#06d6a0' },
                      }}
                    />
                  </Box>
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>Total Clientes: {formatDisplayValue(summaryMetrics.totalClients)}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>Monitorados Ativos: {formatDisplayValue(summaryMetrics.monitorados)}</Typography>
                  </Box>
                </Paper>
              </Box>

              {/* Methodology alerts */}
              <Paper sx={{ p: 3, mb: 4, ...glassCardStyle }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  ⚠️ Indicadores Metodológicos FAST (Carteira Ativa)
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3 }}>
                  <Paper sx={{ p: 2, background: 'rgba(255,255,255,0.05)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>Clientes sem RD Realizada</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: summaryMetrics.clientsWithoutRD > 0 ? '#ff6b6b' : 'inherit' }}>
                      {formatDisplayValue(summaryMetrics.clientsWithoutRD)}
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, background: 'rgba(255,255,255,0.05)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>Clientes sem RE (45 dias)</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: summaryMetrics.clientsWithoutRELast45Days > 0 ? '#ff6b6b' : 'inherit' }}>
                      {formatDisplayValue(summaryMetrics.clientsWithoutRELast45Days)}
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, background: 'rgba(255,255,255,0.05)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>Clientes sem RC no Período</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: summaryMetrics.clientsWithoutRCInPeriod > 0 ? '#ffd166' : 'inherit' }}>
                      {formatDisplayValue(summaryMetrics.clientsWithoutRCInPeriod)}
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, background: 'rgba(255,255,255,0.05)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>Clientes sem RAE no Período</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: summaryMetrics.clientsWithoutRAEInPeriod > 0 ? '#ffd166' : 'inherit' }}>
                      {formatDisplayValue(summaryMetrics.clientsWithoutRAEInPeriod)}
                    </Typography>
                  </Paper>
                </Box>
              </Paper>
            </Box>
          ) : (
            <Alert severity="info" sx={{ mb: 4 }}>Erro ao carregar sumário da empresa.</Alert>
          )
        )}

        {/* Priority Tabs */}
        <Box sx={{ width: '100%', mb: 4 }}>
          <Tabs
            value={tabValue}
            onChange={(_, val) => setTabValue(val)}
            centered
            sx={{
              '& .MuiTabs-indicator': { backgroundColor: 'white' },
              '& .MuiTab-root': {
                color: 'rgba(255,255,255,0.7)',
                fontWeight: 700,
                fontSize: '15px',
                '&.Mui-selected': { color: 'white' },
              },
            }}
          >
            <Tab label={`Críticos (${filteredCritical.length})`} />
            <Tab label={`Em Atenção (${filteredWarning.length})`} />
            <Tab label={`Saudáveis (${filteredHealthy.length})`} />
            <Tab label={`Não Monitorados (${filteredUnmonitored.length})`} />
            <Tab label={`Ranking Prioritário (${filteredRanking.length})`} />
          </Tabs>
        </Box>

        {/* Table list */}
        <TableContainer component={Paper} sx={{ ...glassCardStyle, overflow: 'hidden' }}>
          {activeList.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ opacity: 0.8 }}>
                Nenhum cliente nesta categoria.
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Cliente</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Consultor</TableCell>
                  {tabValue === 4 ? (
                    <>
                      <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Score de Prioridade</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Nível de Risco</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Dias Inativo</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Cumprimento da Meta</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Motivos do Alerta</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={{ color: 'white', fontWeight: 700 }}>Cumprimento da Meta</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Status / Pendências</TableCell>
                    </>
                  )}
                  <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeList.map((client) => {
                  const riskConfig = getRiskLevelStyles(client.riskLevel);
                  return (
                    <TableRow
                      key={client.id_loja}
                      sx={{
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                      }}
                    >
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>{client.razao_social}</TableCell>
                      <TableCell sx={{ color: 'white', opacity: 0.9 }}>{client.consultor}</TableCell>

                      {tabValue === 4 ? (
                        <>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 800 }}>
                            {formatDisplayValue(client.priorityScore)} pts
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={riskConfig.label}
                              size="small"
                              sx={{
                                fontWeight: 800,
                                fontSize: '10px',
                                color: riskConfig.color,
                                backgroundColor: riskConfig.bg,
                                border: `1px solid ${riskConfig.color}`,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ color: 'white' }}>
                            {client.daysSinceLastMeeting !== null ? `${client.daysSinceLastMeeting} dias` : '—'}
                          </TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 700 }}>
                            {formatDisplayValue(client.complianceRate)}%
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', maxWidth: 300 }}>
                              {client.priorityReasons?.map((reason, idx) => (
                                <Chip
                                  key={idx}
                                  label={reason}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    color: 'rgba(255,255,255,0.85)',
                                    borderColor: 'rgba(255,255,255,0.2)',
                                    fontWeight: 500,
                                    fontSize: '10px',
                                  }}
                                />
                              ))}
                            </Box>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell sx={{ width: '25%', color: 'white' }}>
                            {tabValue === 3 ? (
                              <Typography variant="body2" sx={{ opacity: 0.6, fontStyle: 'italic' }}>
                                Monitoramento ainda não iniciado
                              </Typography>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={client.complianceRate || 0}
                                  sx={{
                                    flexGrow: 1,
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                    '& .MuiLinearProgress-bar': {
                                      backgroundColor:
                                        client.complianceRate > 70
                                          ? '#06d6a0'
                                          : client.complianceRate > 40
                                          ? '#ffd166'
                                          : '#ff6b6b',
                                    },
                                  }}
                                />
                                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 32 }}>
                                  {formatDisplayValue(client.complianceRate)}%
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ color: 'white' }} align="center">
                            {tabValue === 3 ? (
                              <Chip
                                label="Inativo"
                                color="default"
                                size="small"
                                sx={{ fontWeight: 700 }}
                              />
                            ) : (
                              <Chip
                                label={`${client.totalPending} Pendentes`}
                                color={client.totalPending > 0 ? (tabValue === 0 ? 'error' : 'warning') : 'success'}
                                size="small"
                                sx={{ fontWeight: 700 }}
                              />
                            )}
                          </TableCell>
                        </>
                      )}

                      <TableCell align="center">
                        <Tooltip title="Inspecionar cliente">
                          <IconButton
                            color="inherit"
                            onClick={() => handleInspectClient(client)}
                            sx={{
                              color: 'white',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        </Box>
      </Container>

      {/* Dialog for Client Journey Details & Monitor Config */}
      <Dialog
        open={detailOpen}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(25, 30, 45, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            color: 'white',
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.12)', pb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Detalhamento & Configuração do Cliente
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
            {selectedClient?.razao_social} | Consultor: {selectedClient?.consultor}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : detailData ? (
            <Box>
              {/* Monitoring Date configuration form */}
              <Paper
                sx={{
                  p: 2.5,
                  mb: 4,
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  color: 'white',
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  ⚙️ Configuração de Monitoramento
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 3 }}>
                  <TextField
                    label="Data de Início do Monitoramento"
                    type="date"
                    value={monitoringDate}
                    onChange={(e) => setMonitoringDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      minWidth: 250,
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                        '&.Mui-focused fieldset': { borderColor: 'white' },
                      },
                      '& .MuiInputLabel-root': { color: 'white' },
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={savingDate ? <CircularProgress size={20} color="inherit" /> : <Save />}
                    onClick={handleSaveMonitoringDate}
                    disabled={savingDate}
                    sx={{
                      height: 48,
                      px: 3,
                      borderRadius: '8px',
                      fontWeight: 700,
                    }}
                  >
                    {savingDate ? 'Salvando...' : 'Salvar Data'}
                  </Button>
                </Box>
                {!detailData.data_inicio_monitoramento && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#ffd166' }}>
                    * Este cliente não está sendo monitorado. Defina uma data acima para ativar a jornada de conformidade.
                  </Typography>
                )}
              </Paper>

              {detailData.data_inicio_monitoramento ? (
                <>
                  {/* Detailed metrics header */}
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    📋 Indicadores Gerais da Jornada
                  </Typography>
                  <Box sx={{ mb: 4, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                    <Paper sx={{ p: 2, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', mb: 0.5 }}>Monitoramento Iniciado em</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {formatDisplayDate(detailData.data_inicio_monitoramento)}
                      </Typography>
                    </Paper>

                    <Paper sx={{ p: 2, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', mb: 0.5 }}>Nível de Risco</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                        <Chip
                          label={getRiskLevelStyles(detailData.riskLevel).label}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            color: getRiskLevelStyles(detailData.riskLevel).color,
                            backgroundColor: getRiskLevelStyles(detailData.riskLevel).bg,
                            border: `1px solid ${getRiskLevelStyles(detailData.riskLevel).color}`,
                          }}
                        />
                      </Box>
                    </Paper>

                    <Paper sx={{ p: 2, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', mb: 0.5 }}>Último Atendimento</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {detailData.lastMeetingType ? `${detailData.lastMeetingType} (${formatDisplayDate(detailData.lastCompleted)})` : 'Nenhum'}
                      </Typography>
                    </Paper>

                    <Paper sx={{ p: 2, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', mb: 0.5 }}>Inatividade Total</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: (detailData.daysSinceLastMeeting && detailData.daysSinceLastMeeting > 60) ? '#ff6b6b' : 'inherit' }}>
                        {detailData.daysSinceLastMeeting !== null ? `${detailData.daysSinceLastMeeting} dias` : '—'}
                      </Typography>
                    </Paper>
                  </Box>

                  {/* Score section */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Cumprimento da Meta da Jornada:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        {formatDisplayValue(detailData.journeyScore)}%
                      </Typography>
                      <CircularProgress
                        variant="determinate"
                        value={detailData.journeyScore || 0}
                        color={
                          detailData.journeyScore > 70
                            ? 'success'
                            : detailData.journeyScore > 40
                            ? 'warning'
                            : 'error'
                        }
                        size={48}
                      />
                    </Box>
                  </Box>

                  {/* Meetings List */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    {detailData.meetings.map((meeting) => {
                      const isRD = meeting.type === 'RD';
                      const isRA = meeting.type === 'RA';
                      const isRP = meeting.type === 'RP';
                      const isFuture = meeting.expected === 0 && meeting.nextDue !== null;

                      const mStatusLabel = getMeetingStatusLabel(meeting.status, meeting.expected, meeting.nextDue);
                      const mStatusStyles = getMeetingStatusColorStyles(meeting.status, meeting.expected, meeting.nextDue);

                      return (
                        <Paper
                          key={meeting.type}
                          sx={{
                            p: 2,
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: '12px',
                            color: 'white',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                Reunião {meeting.type}
                              </Typography>
                              {!isRA && !isRP && (
                                <Chip
                                  label={mStatusLabel}
                                  size="small"
                                  sx={{
                                    fontWeight: 800,
                                    fontSize: '11px',
                                    color: mStatusStyles.color,
                                    backgroundColor: mStatusStyles.bg,
                                    border: `1px solid ${mStatusStyles.color}`
                                  }}
                                />
                              )}
                            </Box>

                            {/* RA specific layout */}
                            {isRA && (
                              <Box sx={{ py: 1 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 1.5 }}>
                                  <Box>
                                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                                      Realizadas (Total)
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#06d6a0' }}>
                                      {formatDisplayValue(meeting.completed)}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                                      Média por Mês
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                      {(() => {
                                        if (!detailData.data_inicio_monitoramento) return '—';
                                        const start = new Date(detailData.data_inicio_monitoramento);
                                        const ref = new Date(referenceDate);
                                        let months = (ref.getFullYear() - start.getFullYear()) * 12 + (ref.getMonth() - start.getMonth());
                                        if (months <= 0) months = 1;
                                        return (meeting.completed / months).toFixed(1);
                                      })()} / mês
                                    </Typography>
                                  </Box>
                                </Box>
                                {meeting.lastCompleted ? (
                                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 1 }}>
                                    📅 Última realizada: <strong>{formatDisplayDate(meeting.lastCompleted)}</strong> {meeting.daysSinceLastMeeting !== null && `(há ${meeting.daysSinceLastMeeting} dias)`}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" sx={{ opacity: 0.6, fontStyle: 'italic', display: 'block', mt: 1 }}>
                                    Não existe registro de Reunião de Alinhamento (RA) para este cliente.
                                  </Typography>
                                )}
                              </Box>
                            )}

                            {/* RP specific layout */}
                            {isRP && (
                              <Box sx={{ py: 1 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 1.5 }}>
                                  <Box>
                                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                                      Realizadas (Total)
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#06d6a0' }}>
                                      {formatDisplayValue(meeting.completed)}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                                      Tipo de Reunião
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#8b949e' }}>
                                      Oportunística
                                    </Typography>
                                  </Box>
                                </Box>
                                {meeting.lastCompleted ? (
                                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 1 }}>
                                    📅 Última realizada: <strong>{formatDisplayDate(meeting.lastCompleted)}</strong> {meeting.daysSinceLastMeeting !== null && `(há ${meeting.daysSinceLastMeeting} dias)`}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" sx={{ opacity: 0.6, fontStyle: 'italic', display: 'block', mt: 1 }}>
                                    Reunião oportunística sem registros.
                                  </Typography>
                                )}
                              </Box>
                            )}

                            {/* Standard layout for RD and periodic meetings (RE, RC, RI, RAE) */}
                            {!isRA && !isRP && (
                              <>
                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap: 1,
                                    mb: 1.5,
                                  }}
                                >
                                  <Box>
                                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                                      Esperadas
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                      {formatDisplayValue(meeting.expected)}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                                      Realizadas
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#06d6a0' }}>
                                      {formatDisplayValue(meeting.completed)}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                                      Pendentes
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: meeting.pending > 0 ? '#ff6b6b' : 'inherit' }}>
                                      {formatDisplayValue(meeting.pending)}
                                    </Typography>
                                  </Box>
                                </Box>

                                <Box sx={{ mb: 1.5 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ opacity: 0.6 }}>Cumprimento da Meta</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{formatDisplayValue(meeting.compliance)}%</Typography>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={meeting.compliance || 0}
                                    sx={{
                                      height: 4,
                                      borderRadius: 2,
                                      backgroundColor: 'rgba(255,255,255,0.1)',
                                      '& .MuiLinearProgress-bar': {
                                        backgroundColor: meeting.compliance > 70 ? '#06d6a0' : meeting.compliance > 40 ? '#ffd166' : '#ff6b6b'
                                      }
                                    }}
                                  />
                                </Box>
                              </>
                            )}
                          </Box>

                          {!isRA && !isRP && (
                            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                              {meeting.lastCompleted && (
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                  📅 Última realizada: <strong>{formatDisplayDate(meeting.lastCompleted)}</strong> {meeting.daysSinceLastMeeting !== null && `(há ${meeting.daysSinceLastMeeting} dias)`}
                                </Typography>
                              )}
                              
                              {!isRD && (
                                <>
                                  {meeting.lastDue && (
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                      🔔 Último vencimento: <strong>{formatDisplayDate(meeting.lastDue)}</strong>
                                    </Typography>
                                  )}
                                  {meeting.nextDue && (
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                      🚀 {isFuture ? 'Primeira obrigação prevista' : 'Próximo vencimento'}: <strong>{formatDisplayDate(meeting.nextDue)}</strong>
                                    </Typography>
                                  )}
                                  {meeting.daysOverdue > 0 && (
                                    <Typography variant="caption" sx={{ color: '#ff8b8b', fontWeight: 650 }}>
                                      ⚠️ Atraso previsto: {meeting.daysOverdue} dias
                                    </Typography>
                                  )}
                                </>
                              )}
                            </Box>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                </>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Insira uma data de monitoramento acima e clique em "Salvar Data" para carregar as métricas de reuniões deste cliente.
                </Alert>
              )}
            </Box>
          ) : (
            <Alert severity="error">Falha ao obter detalhamento do cliente.</Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.12)', px: 3, py: 2 }}>
          <Button onClick={handleCloseDetail} variant="outlined" color="inherit" sx={{ borderRadius: '8px' }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Toast */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default JornadaDoCliente;


